import React, { useCallback, useEffect, useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';

import { scrollEditorHeadingIntoView } from '@confluence/editor-features/entry-points/scrollEditorHeadingIntoView.ts';
import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { expValEquals } from '@confluence/feature-experiments';
import type { FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { useShowSuggestionsPanel } from '@confluence/suggestions-panel/entry-points/useShowSuggestionsPanel.ts';

import { useSharedAIState } from './hooks/useSharedAIState.ts';

interface ObjectSidebarControlFullPageEditorProps {
	editorApi: FullPageEditorPresetAPI | undefined;
	hasAiButton: boolean;
	hasAudioButton: boolean;
	contentId: string;
	spaceKey: string;
	contentType: string;
	contentTitle: string;
	isUnpublishedDraft?: boolean;
	disabled?: boolean;
	isEditorEmpty?: boolean;
	isMarkdownMode?: boolean;
	isMarkdownModeSyntaxViewToggled?: boolean;
}

export function ObjectSidebarControlFullPageEditor({
	editorApi,
	hasAiButton,
	hasAudioButton,
	contentId,
	spaceKey,
	contentType,
	contentTitle,
	isUnpublishedDraft,
	disabled,
	isEditorEmpty,
	isMarkdownMode,
	isMarkdownModeSyntaxViewToggled,
}: ObjectSidebarControlFullPageEditorProps): React.JSX.Element {
	const { aiIsStreaming } = useSharedAIState(editorApi);

	const hasAiSuggestions = !!editorApi?.aiSuggestions;

	const thinkingStatus = useSharedPluginStateSelector(editorApi, 'aiSuggestions.thinkingStatus', {
		disabled: !hasAiSuggestions,
	});

	const { isSuggestionsPanelShown, showSuggestionsPanel } = useShowSuggestionsPanel();

	useEffect(() => {
		editorApi?.aiSuggestions?.actions.setUIVisibility(isSuggestionsPanelShown);
	}, [editorApi, isSuggestionsPanelShown]);

	const onReviewButtonClick = useCallback(() => {
		showSuggestionsPanel({ entryPoint: 'suggestionsPanel' });
	}, [showSuggestionsPanel]);

	const aiSuggestionsButtonActions = useMemo(
		() =>
			hasAiSuggestions &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
				? { onStartThinking: onReviewButtonClick, isThinking: thinkingStatus === 'thinking' }
				: undefined,
		[hasAiSuggestions, onReviewButtonClick, thinkingStatus],
	);

	const getAdfDocument = useCallback((): Promise<DocNode | null> => {
		return new Promise((resolve) => {
			if (editorApi?.core?.actions?.requestDocument) {
				editorApi.core.actions.requestDocument((doc) => {
					resolve(doc as DocNode);
				});
			} else {
				resolve(null);
			}
		});
	}, [editorApi]);

	return (
		<ObjectSidebarControl
			key="object-sidebar-control"
			hasCommentsButton
			hasDetailsButton
			hasLoomButton
			hasAiButton={hasAiButton}
			hasRemixButton
			hasAudioButton={hasAudioButton}
			aiSuggestionsButtonActions={aiSuggestionsButtonActions}
			aiIsStreaming={aiIsStreaming}
			contentId={contentId}
			spaceKey={spaceKey}
			contentType={contentType}
			contentTitle={contentTitle}
			isUnpublishedDraft={isUnpublishedDraft}
			disabled={disabled}
			isEditorEmpty={isEditorEmpty}
			isMarkdownMode={isMarkdownMode}
			isMarkdownModeSyntaxViewToggled={isMarkdownModeSyntaxViewToggled}
			getAdfDocument={getAdfDocument}
			editorApi={editorApi}
			scrollToEditorHeading={scrollEditorHeadingIntoView}
		/>
	);
}
